import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The IANA zone used throughout the admin UI.
 * Overridden at runtime by the value returned by GET /api/admin/email/settings.
 * Kept as a module-level variable so pages can call `setAppTimezone` once and
 * every subsequent formatting call picks it up.
 */
let APP_TZ = "Asia/Jerusalem";

export function setAppTimezone(tz) {
  if (tz && typeof tz === "string") APP_TZ = tz;
}

export function getAppTimezone() {
  return APP_TZ;
}

/**
 * Convert a datetime-local input value (e.g. "2026-02-13T10:00") — which the
 * user typed as a WALL CLOCK time in the app timezone — into a UTC ISO string
 * suitable for the API.
 *
 * Naive alternative `new Date(local).toISOString()` uses the browser timezone,
 * which is wrong for an admin traveling abroad.
 */
export function wallClockToUtcISO(localStr, tz = APP_TZ) {
  if (!localStr) return null;
  // dayjs.tz(str, tz) parses the string as if it were in the given zone.
  return dayjs.tz(localStr, tz).utc().toISOString();
}

/**
 * Inverse of `wallClockToUtcISO`. Given a UTC ISO string from the API, return
 * a "YYYY-MM-DDTHH:mm" string that fits into a `<input type="datetime-local">`
 * in the app timezone.
 */
export function utcISOToWallClock(iso, tz = APP_TZ) {
  if (!iso) return "";
  return dayjs.utc(iso).tz(tz).format("YYYY-MM-DDTHH:mm");
}

/**
 * Pretty display of a UTC ISO date in the app timezone, in Hebrew locale.
 * Example: "13.02.2026, 10:00" instead of the browser default that might
 * show a different TZ.
 */
export function formatInAppTz(iso, opts = {}) {
  if (!iso) return "—";
  const format = opts.format || "DD.MM.YYYY, HH:mm";
  return dayjs.utc(iso).tz(APP_TZ).format(format);
}

/**
 * Human-friendly label for the current app timezone including offset.
 * Example: "Asia/Jerusalem (UTC+03:00, IDT)"
 */
export function tzLabel() {
  const now = dayjs().tz(APP_TZ);
  return `${APP_TZ} (${now.format("Z")})`;
}
